<template>
  <el-dialog
    :model-value="modelValue"
    title="选择小程序"
    width="780px"
    append-to-body
    @update:model-value="emit('update:modelValue', $event)"
  >
    <p class="font-medium text-slate-700 mb-3">物料配置</p>

    <!-- 按钮区：查看模式 / 编辑模式 -->
    <div class="flex mb-2">
      <template v-if="!isEditing">
        <el-button type="primary" @click="onAdd">添加小程序</el-button>
        <el-button type="primary" plain @click="onBatchEdit">批量编辑</el-button>
        <el-button type="danger" :disabled="selectedRows.length === 0" @click="onDeleteSaved">
          删除
        </el-button>
      </template>
      <template v-else>
        <el-button type="primary" s plain @click="addRow">增加多条</el-button>
        <el-button type="danger" :disabled="selectedRows.length === 0" @click="onDeleteRow">
          删除
        </el-button>
        <el-button type="primary" :loading="saveLoading" @click="onSave">保存</el-button>
      </template>
    </div>

    <DataTableWithPagination
      v-model:page="page"
      v-model:page-size="pageSize"
      :data="tableRows"
      :loading="loading"
      :total="total"
      :show-column-settings="false"
      highlight-current-row
      padding="0"
      @page-change="loadList"
      @size-change="loadList"
      @selection-change="(rows: WxMiniRow[]) => (selectedRows = rows)"
      @current-change="
        (row: WxMiniRow | null) => {
          if (!isEditing) selectedMini = row;
        }
      "
    >
      <el-table-column type="selection" width="44" />
      <el-table-column label="appid" width="120">
        <template #default="{ row }">
          <el-input v-if="isEditing" v-model="row.app_id" placeholder="appid" />
          <span v-else>{{ row.app_id }}</span>
        </template>
      </el-table-column>
      <el-table-column label="小程序路径">
        <template #default="{ row }">
          <el-input v-if="isEditing" v-model="row.page_path" placeholder="路径" />
          <span v-else>{{ row.page_path }}</span>
        </template>
      </el-table-column>
      <el-table-column label="卡片标题" width="140">
        <template #default="{ row }">
          <el-input v-if="isEditing" v-model="row.title" placeholder="卡片标题" />
          <span v-else>{{ row.title }}</span>
        </template>
      </el-table-column>
      <el-table-column label="上传图片" width="100">
        <template #default="{ row }">
          <el-upload
            v-if="isEditing"
            :show-file-list="false"
            :auto-upload="false"
            accept="image/*"
            @change="(f) => f.raw && onImageChange(row, f.raw)"
          >
            <el-avatar shape="square" :size="32" :src="row.image_url || undefined">
              <el-icon><Plus /></el-icon>
            </el-avatar>
          </el-upload>
          <el-avatar v-else-if="row.image_url" shape="square" :size="32" :src="row.image_url" />
          <el-avatar v-else shape="square" :size="32">
            <el-icon><Picture /></el-icon>
          </el-avatar>
        </template>
      </el-table-column>
    </DataTableWithPagination>

    <template #footer>
      <el-button @click="onCancel">取消</el-button>
      <el-button v-if="!isEditing" type="primary" :disabled="!confirmTarget" @click="onConfirm">
        确认
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { Picture, Plus } from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { getUploadUrl } from '@/api/common';
import { toBearerHeader } from '@/utils/token';
import DataTableWithPagination from '@/components/DataTableWithPagination.vue';
import type { WxMiniRow } from '@/types/chatBox';

const props = defineProps<{
  modelValue: boolean;
  accountId?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', val: boolean): void;
  (e: 'confirm', mini: Record<string, unknown>): void;
}>();

const loading = ref(false);
const saveLoading = ref(false);
const isEditing = ref(false);
const tableRows = ref<WxMiniRow[]>([]);
const selectedRows = ref<WxMiniRow[]>([]);
const selectedMini = ref<WxMiniRow | null>(null);

/**
 * 待发送的小程序：优先取点击行，其次取勾选。
 * 勾选框本是给批量编辑/删除用的多选，但用户看到勾选框就会用它来选发送对象，
 * 只认点击行会让「明明勾上了却不能确认」；勾多行时发送对象不唯一，仍视为未选。
 */
const confirmTarget = computed<WxMiniRow | null>(
  () =>
    selectedMini.value ??
    (selectedRows.value.length === 1 ? (selectedRows.value[0] ?? null) : null),
);
const total = ref(0);
const page = ref(1);
const pageSize = ref(10);

const uploadUrl = getUploadUrl();
const authToken = toBearerHeader(localStorage.getItem('token'));

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      isEditing.value = false;
      selectedMini.value = null;
      selectedRows.value = [];
      page.value = 1;
      loadList();
    }
  },
);

async function loadList() {
  loading.value = true;
  try {
    const { channelMaterialsApi, parseMaterialContent } =
      await import('@/api/smartService/channelMaterials');
    const { list, total: t } = await channelMaterialsApi
      .list({
        account_id: props.accountId || '',
        type: 9,
        page: page.value,
        per_page: pageSize.value,
      })
      .catch(() => ({ list: [], total: 0 }));
    tableRows.value = (list as Record<string, unknown>[]).map((r) => {
      const c = parseMaterialContent(r.content);
      return {
        id: r.id as number | undefined,
        app_id: (c.app_id as string) || '',
        page_path: (c.page_path as string) || '',
        title: (c.title as string) || '',
        image_url: ((c.thumb_url as string) || (c.image_url as string)) ?? '',
      };
    });
    total.value = t;
  } finally {
    loading.value = false;
  }
}

function emptyRow(): WxMiniRow {
  return { app_id: '', page_path: '', title: '', image_url: '', isNew: true };
}

function onAdd() {
  isEditing.value = true;
  selectedRows.value = [];
  tableRows.value.push(emptyRow());
}

function onBatchEdit() {
  isEditing.value = true;
  selectedRows.value = [];
}

function addRow() {
  tableRows.value.push(emptyRow());
}

function onDeleteRow() {
  tableRows.value = tableRows.value.filter((r) => !selectedRows.value.includes(r));
  selectedRows.value = [];
}

async function onDeleteSaved() {
  if (selectedRows.value.length === 0) return;
  await ElMessageBox.confirm('确定删除选中的小程序吗？', '提示', { type: 'warning' });
  const ids = selectedRows.value.map((r) => r.id).filter((v): v is number => typeof v === 'number');
  const { channelMaterialsApi } = await import('@/api/smartService/channelMaterials');
  await Promise.all(ids.map((id) => channelMaterialsApi.delete(id)));
  ElMessage.success('删除成功');
  loadList();
}

async function onImageChange(row: WxMiniRow, file: File) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(uploadUrl, {
    method: 'POST',
    headers: { Authorization: authToken || '' },
    body: form,
  });
  const data = (await res.json()) as { code?: number; data?: { url?: string } };
  if (data?.code === 0 && data?.data?.url) {
    row.image_url = data.data.url;
  }
}

async function onSave() {
  const rows = tableRows.value.filter((r) => r.app_id.trim() || r.title.trim());
  if (rows.length === 0) {
    ElMessage.warning('请至少填写一条数据');
    return;
  }
  saveLoading.value = true;
  try {
    const { channelMaterialsApi } = await import('@/api/smartService/channelMaterials');
    await Promise.all(
      rows.map((row) => {
        const content = {
          app_id: row.app_id,
          page_path: row.page_path,
          title: row.title,
          thumb_url: row.image_url,
        };
        if (row.id) {
          return channelMaterialsApi.update(row.id, { content });
        }
        return channelMaterialsApi.create({
          account_id: props.accountId || '',
          name: row.title || '小程序卡片',
          type: 9,
          content,
        });
      }),
    );
    ElMessage.success('保存成功');
    isEditing.value = false;
    selectedRows.value = [];
    loadList();
  } finally {
    saveLoading.value = false;
  }
}

function onCancel() {
  if (isEditing.value) {
    isEditing.value = false;
    loadList();
  } else {
    emit('update:modelValue', false);
  }
}

function onConfirm() {
  if (!confirmTarget.value) return;
  emit('confirm', confirmTarget.value as unknown as Record<string, unknown>);
  emit('update:modelValue', false);
}
</script>
